import {
  PencilSimple,
  CheckCircle,
  RocketLaunch,
  Star,
  Crown,
  Icon,
} from '@phosphor-icons/react';
import { TierType } from '../../../types/apiTypes';

// Tier display configuration matching marketing content
export const TIER_CONFIG: Record<
  TierType,
  {
    name: string;
    price: string;
    tagline: string;
    icon: Icon;
  }
> = {
  [TierType.STARTER]: {
    name: 'Starter',
    price: 'Free',
    tagline:
      'Essential TACH tools with the flexibility to add services as needed.',
    icon: RocketLaunch,
  },
  [TierType.CORE]: {
    name: 'Core',
    price: '1.19% of revenue deposits',
    tagline: 'Cash back rewards and great rates on the services you use.',
    icon: Star,
  },
  [TierType.PRO]: {
    name: 'Pro',
    price: '1.49% of revenue deposits',
    tagline: 'Maximum cash back and premium fleet-level benefits included.',
    icon: Crown,
  },
};

// The same five rows, same order, identical text, on every card.
export const TIER_BASE_FEATURES: string[] = [
  'TACH Business Account',
  'Unlimited Commercial Cards',
  'Revenue-Based Cash Advances',
  'Smart Budgeting',
  'Bridgestone Tire Discounts',
];

// Only what that tier adds beyond the base list — empty means no PLUS divider.
export const TIER_PLUS_FEATURES: Record<TierType, string[]> = {
  [TierType.STARTER]: [],
  [TierType.CORE]: [
    '2% Fuel Cashback',
    '1% Cashback on Other Purchases',
    'Better Cash Advance Rates',
    'Discounted Service Fees',
  ],
  [TierType.PRO]: [
    '3% Fuel Cashback',
    '2% Cashback on Other Purchases',
    'Best Cash Advance Rates',
    'Free $0-Deposit Rentals Access',
    'Free 24/7 Roadside Assistance Access',
  ],
};

export const getTierFeatureList = (tier: TierType): string[] => [
  ...TIER_BASE_FEATURES,
  ...TIER_PLUS_FEATURES[tier],
];

interface TierReviewCardProps {
  className?: string;
  tier: TierType | null;
  onEdit: () => void;
}

/**
 * The chosen tier as a read-back (THT-773) — Choose Tier is the only place a tier is picked, so
 * Edit here is local-only navigation back to that step, never a submit of its own.
 */
function TierReviewCard({ className, tier, onEdit }: TierReviewCardProps) {
  const tierConfig = tier ? TIER_CONFIG[tier] : null;

  return (
    <div
      className={`rounded-[4px] border-[1px] border-bordergray p-3 ${className ?? ''}`}
    >
      {/* Header row: Title and change button */}
      <div className="mb-4 flex w-full flex-row items-center justify-between align-middle">
        <h2 className="text-lg font-semibold">Membership Tier</h2>
        <button
          onClick={onEdit}
          className="flex flex-row items-center space-x-1 align-middle text-[16px] font-[700] text-accent underline"
        >
          Edit <PencilSimple size={16} />
        </button>
      </div>

      {tierConfig ? (
        <>
          {/* Tier name and tagline row */}
          <div className="mb-3 flex flex-row items-center gap-2 md:items-baseline md:gap-3">
            <div className="flex flex-shrink-0 flex-row items-center gap-2">
              <tierConfig.icon
                size={24}
                weight="fill"
                className="text-accent md:h-[28px] md:w-[28px]"
              />
              <span className="text-xl font-semibold text-gray-900 md:text-2xl">
                {tierConfig.name}
              </span>
            </div>
            <span className="ml-2 text-xs italic text-gray-500 md:ml-3 md:text-sm">
              {tierConfig.tagline}
            </span>
          </div>

          {/* Features row: horizontal badges with checkmarks */}
          <div className="mx-[-6px] flex flex-row flex-wrap gap-1.5 md:gap-2">
            {(tier ? getTierFeatureList(tier) : []).map((feature, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 rounded-full bg-green-50 px-1 py-px text-xs text-green-700 md:gap-1.5 md:px-3 md:py-1 md:text-sm"
              >
                <CheckCircle
                  size={14}
                  weight="fill"
                  className="hidden text-green-600 md:block"
                />
                {feature}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm italic text-gray-500">No plan selected</p>
      )}
    </div>
  );
}

export default TierReviewCard;
